import {
  AlertMessage,
  AnimatedNavigationSwitch,
  NavigationPage,
} from '@/app/components'
import { Route } from 'react-router-dom'
import { ConfigureVehicle } from './steps/ConfigureVehicle'
import {
  ConfigureDeviceType,
  useConfigureDeviceFlow,
} from './ConfigureDevices.hooks'
import ConfigureCharger from './steps/ConfigureCharger'
import ConfigureThermostat from './steps/ConfigureThermostat'
import { useNavigation } from '@/app/hooks'
import ConfigureMobilePushNotifications from './steps/ConfigureMobilePushNotifications'
import { useEffect } from 'react'
import LoadingAnimation from '@/components/LoadingAnimation/LoadingAnimation'
import { Button } from '@/components'
import { match } from 'ts-pattern'
import { clearPendingOnComplete } from './onCompleteRegistry'

export function ConfigureDevicesRoutes() {
  const { progress } = useConfigureDeviceFlow()
  const navigate = useNavigation()
  const onCloseClick = () => {
    // Drop the pending completion callback — the user is bailing out of the
    // flow, so the caller's "on success" hook (e.g., completeOnboarding)
    // should not fire.
    clearPendingOnComplete()
    navigate.push('/devices')
  }
  return (
    <NavigationPage
      id={'configure-device-root'}
      backDisabled={progress?.completedSteps === 0}
      onCloseClick={onCloseClick}
      navigationOptions={['back', 'close']}
      progressBar={
        progress
          ? { ...progress, completedSteps: progress.completedSteps + 1 }
          : undefined
      }
    >
      <AnimatedNavigationSwitch>
        <Route path="/configure-devices/init">
          <ConfigureDevicesInit />
        </Route>
        <Route path="/configure-devices/:deviceId/vehicle">
          <ConfigureVehicle />
        </Route>
        <Route path="/configure-devices/:deviceId/thermostat">
          <ConfigureThermostat />
        </Route>
        <Route path="/configure-devices/:deviceId/charger">
          <ConfigureCharger />
        </Route>
        <Route path="/configure-devices/:deviceId/push-notifications">
          <ConfigureMobilePushNotifications />
        </Route>
      </AnimatedNavigationSwitch>
      {progress && !location.pathname.includes('/init') && (
        <div className="mt-8">
          <ConfigureDevicesFooter />
        </div>
      )}
    </NavigationPage>
  )
}

function ConfigureDevicesFooter() {
  const { nextStep, progress, steps } = useConfigureDeviceFlow()
  if (!progress) {
    return null
  }
  const buttonText =
    progress.completedSteps + 1 === progress.totalSteps
      ? 'Complete Setup'
      : 'Continue'
  const currentStepDeviceType = steps?.[progress.completedSteps].deviceType
  const alertMessageText = match({ currentStepDeviceType })
    // The mobile download step is purely informational — nothing on it is
    // a "setting", so the reminder doesn't apply. Figma omits it.
    .with({ currentStepDeviceType: ConfigureDeviceType.Mobile }, () => null)
    .otherwise(
      () => 'You can always adjust these settings later via the “Devices” tab.',
    )
  return (
    <div className="flex flex-col gap-8">
      {alertMessageText && (
        <AlertMessage variant="info">{alertMessageText}</AlertMessage>
      )}
      <Button id={'configure-devices-next-step'} onClick={nextStep}>
        {buttonText}
      </Button>
    </div>
  )
}

function ConfigureDevicesInit() {
  const { steps } = useConfigureDeviceFlow()
  const navigate = useNavigation()
  useEffect(() => {
    if (!steps) {
      return
    }
    if (steps.length === 0) {
      navigate.push('/devices')
      return
    }
    navigate.pushRetainingParams(steps[0].url)
  }, [steps])
  return (
    <div className="flex justify-center items-center h-full">
      <LoadingAnimation type={'falling'} />
    </div>
  )
}
